import { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth as useClerkAuth, useClerk } from "@clerk/clerk-react";

const AUTH_TIMEOUT_MS = 10_000;

interface User {
  id: string;
  slug: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "participant" | "advisor" | "program_admin" | "content_admin" | "admin";
  timezone?: string;
  /** Present only when the active session was created via an admin
   *  "Login as" impersonation (Clerk actor token). */
  impersonatedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  authFailed: boolean;
  authTimedOut: boolean;
  login: () => void;
  logout: () => Promise<void>;
  register: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const clerk = useClerk();
  const queryClient = useQueryClient();
  const [authTimedOut, setAuthTimedOut] = useState(false);

  const { data: user, isLoading, isError } = useQuery<User | null>({
    queryKey: ["/api/auth/me"],
    queryFn: async () => {
      // Force a fresh token each time — avoids stale token causing persistent 401
      const token = await clerk.session?.getToken({ skipCache: true });
      const res = await fetch("/api/auth/me", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Auth request failed: ${res.status}`);
      return res.json();
    },
    enabled: isLoaded && isSignedIn === true,
    // Retry up to 3 times with a 1.5 s delay so transient errors
    // (Clerk JIT-create, brief DB hiccup) resolve before we give up.
    retry: 3,
    retryDelay: 1500,
  });

  const login = () => {
    window.location.href = "/login";
  };

  const register = () => {
    window.location.href = "/login";
  };

  const logout = async () => {
    await clerk.signOut();
    queryClient.clear();
  };

  // Stay in loading state while Clerk is initialising OR while signed in with
  // no user yet — regardless of error state.  Dropping loading=true when
  // isError=true lets ProtectedRoute redirect to /login while Clerk still says
  // isSignedIn=true, which triggers an infinite loop and crashes ScrollToTop.
  // Only lower the guard when Clerk itself flips isSignedIn to false.
  const loading = !isLoaded || (isSignedIn === true && !user);
  const authFailed = isLoaded && isSignedIn === true && !user && isError;
  // Clerk itself can fail to initialise (e.g. a CSP misconfiguration blocking
  // its script/XHR calls) — isLoaded then stays false forever with no error
  // to key off. Treat "still not loaded" the same as authFailed for timeout
  // purposes so users get the retry screen instead of an infinite spinner.
  const stuckLoading = !isLoaded || authFailed;

  // If the API has persistently failed, or Clerk never finishes initialising,
  // and we're still stuck in loading, show a timeout error after
  // AUTH_TIMEOUT_MS rather than spinning forever.
  useEffect(() => {
    if (!stuckLoading) {
      setAuthTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setAuthTimedOut(true), AUTH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [stuckLoading]);

  return (
    <AuthContext.Provider
      value={{
        user: (user as User) || null,
        isLoading: loading,
        authFailed,
        authTimedOut,
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
